import {
  disconnect,
  sendChat,
  sendChatDelete,
  sendStopMatch,
  sendTurnRadiusSelect,
  togglePause
} from './network-client.js';
import {
  DEFAULT_CONFIG,
  queryParam,
  TURN_RELEASE_GRACE_MS,
  SFX
} from './engine/config.js';
import {
  powerupsCanvas,
  trailCanvas,
  headsCanvas,
  netStatusEl,
  stopMatchBtnEl,
  lobbyPeekBtnEl,
  muteBtnEl,
  muteBtnLabelEl,
  muteBtnIconEl,
  pauseBtnEl,
  pauseBtnLabelEl,
  pauseBtnIconEl,
  settingsBtnEl,
  lobbySettingsBtnEl,
  lobbyNavSettingsBtnEl,
  leaveBtnEl,
  devReloadBtnEl,
  chatFeedEl,
  chatFormEl,
  chatGuestLockEl,
  chatFormFieldsEl,
  chatInputEl,
  chatSendBtnEl,
  settingsModalEl,
  settingsCloseBtnEl,
  settingsSoundVolumeEl,
  settingsSoundVolumeValueEl,
  settingsTurnRadiusEl,
  settingsTurnRadiusValueEl,
  settingsControlLeftBtnEl,
  settingsControlRightBtnEl,
  settingsResolutionEl,
  settingsResolutionValueEl,
  settingsInputLagEl,
  settingsInputLagValueEl,
  settingsKeyTimestampToggleEl,
  settingsPowerupSpawnToggleEl
} from './ui/dom.js';
import { model, ui, keyState, keyIntent, net, lobby, lobbyRuntime, authState } from './engine/state.js';
import { isLobbyChatConnected, sendLobbyChat, sendLobbyChatDelete } from './network/lobby-chat.js';
import {
  settingsUi,
  settings,
  turnBindingLabel,
  keyBindingMatches,
  isBindableControlKeyEvent,
  turnBindingFromEvent,
  setControlCaptureTarget,
  persistSettings,
  keyMatchesTurnLeft,
  keyMatchesTurnRight
} from './ui/settings.js';
import { clamp, setTextIfChanged } from './utils/utils.js';
import { beginRenderFrame, endRenderFrame, serverNowMs } from './engine/time.js';
import {
  audioState,
  updateAppFocusState,
  stopAllSfx,
  setSoundEnabled,
  playSound,
  markAudioInteraction,
  flushScheduledStartSfx
} from './audio/audio.js';
import {
  LOBBY_CHAT_FEED_ID,
  resetChatFeed,
  setupChatChannels,
  setupChatEmotes,
  setupChatAutocomplete,
  setChatDeleteHandler,
  isLobbyChatSelected,
  isChatAdmin,
  knownChatNames,
  setLocalChatMuted,
  showChatNotice
} from './ui/chat.js';
import { chatInputText, clearChatInput, setChatInputEnabled } from './ui/chat-input.js';
import { parseChatCommand } from '../core/chat-commands.js';
import {
  PLAYER_TURN_RADIUS_MAX_PERCENT,
  PLAYER_TURN_RADIUS_MIN_PERCENT,
  normalizePlayerTurnRadiusPercent
} from '../../shared/game-balance.js';
import { initHighScores } from './ui/highscores.js';
import { initProfile } from './ui/profile.js';
import { setStatus } from './ui/status.js';
import { renderScores, renderTargetDisplay } from './ui/scoreboard.js';
import {
  renderRoundOverlay,
  updateDelayedMatchEndOverlay,
  updateOverlayCountdown
} from './ui/overlay.js';
import { initFocusOverlay } from './ui/focus-overlay.js';
import {
  setupRenderBackend,
  setCanvasSize,
  fitStageToViewport,
  clearField,
  applyOps,
  dispatchRenderFrame,
  dispatchPlayerFrame,
  syncRenderPrefs
} from './render/render.js';
import {
  setLobbyDeps,
  setupLobbyUi,
  syncLobbyCountdownFromState,
  renderLobbyStartCountdown,
  isLobbyVisible,
  returnToLobbyBrowser,
  openWaitingRoomPeek,
  setLobbyVisible,
  renderLobby
} from './ui/lobby.js';
import { setupAuthUi, bootstrapAuthAndStart } from './network/auth.js';
import { setNetworkDeps, setupNetworkHandlers, startNetworking, stopTimers } from './network/network.js';
import {
  applyResolvedTurnKeys,
  inputHeartbeatIntervalMs,
  clearAllInputRetries,
  applyState,
  applyAckSeq,
  sendCurrentInput
} from './input/input.js';
import {
  pushSnapshot,
  updatePredictionAnchor,
  sampleRenderState,
  applyLocalPrediction
} from './engine/prediction.js';
import { isSoloMatch } from './engine/state-helpers.js';

// Replay capture/playback is dev tooling; ui/replay-controls.js is loaded via
// dynamic import in dev only, and swaps in the real implementations here.
const replay = {
  isPlaybackActive: () => false,
  renderFrame: () => false,
  sidebarSig: () => '',
  updateSidebarElements: () => {},
  handleChatCommand: () => false,
  onLiveCommand: () => {},
  setupSidebar: () => {}
};

const TURN_RADIUS_REQUEST_DEBOUNCE_MS = 800;
let turnRadiusRequestTimer = 0;

function setToggleButtonState(button, enabled) {
  if (!button) return;
  const active = Boolean(enabled);
  button.classList.toggle('on', active);
  button.classList.toggle('off', !active);
  button.setAttribute('aria-checked', active ? 'true' : 'false');
}

function setSettingValueDisplay(element, value) {
  if (!element) return;
  const text = String(value);
  if ('value' in element) {
    element.value = text;
    return;
  }
  element.textContent = text;
}

function syncSettingsModalUi() {
  const leftCapturing = settingsUi.controlCaptureTarget === 'left';
  const rightCapturing = settingsUi.controlCaptureTarget === 'right';
  if (settingsControlLeftBtnEl) {
    settingsControlLeftBtnEl.textContent = leftCapturing
      ? 'Press...'
      : turnBindingLabel(settings.turnLeftKey, 'LEFT');
    settingsControlLeftBtnEl.classList.toggle('is-capturing', leftCapturing);
  }
  if (settingsControlRightBtnEl) {
    settingsControlRightBtnEl.textContent = rightCapturing
      ? 'Press...'
      : turnBindingLabel(settings.turnRightKey, 'RIGHT');
    settingsControlRightBtnEl.classList.toggle('is-capturing', rightCapturing);
  }
  if (settingsSoundVolumeEl) {
    settingsSoundVolumeEl.value = String(settings.soundVolume);
  }
  setSettingValueDisplay(settingsSoundVolumeValueEl, settings.soundVolume);
  if (settingsTurnRadiusEl) {
    settingsTurnRadiusEl.value = String(net.turnRadiusPercent);
    settingsTurnRadiusEl.disabled = !net.connected || lobby.mode !== 'room';
  }
  if (settingsTurnRadiusValueEl) {
    if (document.activeElement !== settingsTurnRadiusValueEl) {
      settingsTurnRadiusValueEl.value = String(net.turnRadiusPercent);
    }
    settingsTurnRadiusValueEl.disabled = !net.connected || lobby.mode !== 'room';
  }
  if (settingsResolutionEl) {
    settingsResolutionEl.value = String(settings.resolutionPercent);
  }
  setSettingValueDisplay(settingsResolutionValueEl, settings.resolutionPercent);
  if (settingsInputLagEl) {
    settingsInputLagEl.value = String(settings.inputLagReductionMs);
  }
  setSettingValueDisplay(settingsInputLagValueEl, settings.inputLagReductionMs);

  setToggleButtonState(settingsKeyTimestampToggleEl, settings.useKeyPressTimestamp);
  setToggleButtonState(settingsPowerupSpawnToggleEl, settings.powerupSpawnSounds);
}

function applyCapturedTurnBinding(event) {
  const target = settingsUi.controlCaptureTarget;
  if (target !== 'left' && target !== 'right') return false;
  if (event.key === 'Escape') {
    setControlCaptureTarget(null);
    syncSettingsModalUi();
    return true;
  }
  if (!isBindableControlKeyEvent(event)) {
    return true;
  }
  const nextBinding = turnBindingFromEvent(event);
  if (!nextBinding) {
    return true;
  }
  if (target === 'left') {
    settings.turnLeftKey = nextBinding;
  } else {
    settings.turnRightKey = nextBinding;
  }
  clearInput();
  persistSettings();
  setControlCaptureTarget(null);
  syncSettingsModalUi();
  return true;
}

function setSettingsModalVisible(show) {
  if (!settingsModalEl) return;
  settingsUi.visible = Boolean(show);
  if (!settingsUi.visible) {
    setControlCaptureTarget(null);
  }
  settingsModalEl.classList.toggle('hidden', !settingsUi.visible);
  settingsModalEl.setAttribute('aria-hidden', settingsUi.visible ? 'false' : 'true');
  if (settingsUi.visible) {
    syncSettingsModalUi();
  }
  ui.controlsDirty = true;
}

function settingsModalVisible() {
  return settingsUi.visible && Boolean(settingsModalEl && !settingsModalEl.classList.contains('hidden'));
}

// The stop button ends a solo match early. Match size, not room capacity,
// decides: an FFA/team room that started with one participant is a solo match
// for scoring and must expose the same control as the one-seat preset.
// Whether there is a live match to step away FROM. Deliberately broader than
// canStopMatchNow, which is solo-only: leaving the arena for a moment is
// something any player in any room can want to do.
function matchIsRunningNow() {
  if (!net.connected || lobby.mode !== 'room') return false;
  const status = model.state?.status || {};
  return Boolean(status.running) && !status.gameOver;
}

function canStopMatchNow() {
  if (!net.connected || lobby.mode !== 'room') return false;
  if (lobby.iAmSpectator) return false;
  const status = model.state?.status || {};
  return Boolean(status.running)
    && !status.gameOver
    && isSoloMatch(model.config, model.state);
}

function updateSidebarControls() {
  const gameplayConnected = net.connected;
  const serverPaused = Boolean(model.state?.status?.paused);
  const showStopMatch = canStopMatchNow();
  const showMatchNav = matchIsRunningNow();
  const nextSig = [
    audioState.enabled ? 1 : 0,
    gameplayConnected ? 1 : 0,
    isLobbyChatConnected() ? 1 : 0,
    lobbyRuntime.returnToLobbyInProgress ? 1 : 0,
    serverPaused ? 1 : 0,
    settingsUi.visible ? 1 : 0,
    showStopMatch ? 1 : 0,
    showMatchNav ? 1 : 0,
    isLobbyChatSelected() ? 'lobby-chat' : 'room-chat',
    lobby.iAmMuted ? 1 : 0,
    replay.sidebarSig()
  ].join('|');
  if (ui.controlsSig === nextSig) {
    return;
  }
  ui.controlsSig = nextSig;

  if (muteBtnEl) {
    const sfxLabel = audioState.enabled ? 'SFX ON' : 'SFX OFF';
    if (muteBtnLabelEl) {
      setTextIfChanged(muteBtnLabelEl, sfxLabel);
    }
    muteBtnEl.setAttribute('aria-label', sfxLabel);
    muteBtnEl.title = sfxLabel;
    muteBtnEl.classList.toggle('is-muted', !audioState.enabled);
    if (muteBtnIconEl) {
      muteBtnIconEl.classList.toggle('bi-volume-up-fill', audioState.enabled);
      muteBtnIconEl.classList.toggle('bi-volume-mute-fill', !audioState.enabled);
    }
  }
  if (lobbyPeekBtnEl) lobbyPeekBtnEl.hidden = !showMatchNav;
  if (stopMatchBtnEl) {
    stopMatchBtnEl.hidden = !showStopMatch;
  }
  if (leaveBtnEl) {
    leaveBtnEl.disabled = lobbyRuntime.returnToLobbyInProgress;
  }
  if (pauseBtnEl) {
    const pauseLabel = serverPaused ? 'Resume' : 'Pause';
    if (pauseBtnLabelEl) {
      setTextIfChanged(pauseBtnLabelEl, pauseLabel);
    }
    pauseBtnEl.setAttribute('aria-label', pauseLabel);
    pauseBtnEl.title = pauseLabel;
    pauseBtnEl.classList.toggle('is-paused', serverPaused);
    pauseBtnEl.disabled = lobbyRuntime.returnToLobbyInProgress || !gameplayConnected;
    if (pauseBtnIconEl) {
      pauseBtnIconEl.classList.toggle('bi-pause-fill', !serverPaused);
      pauseBtnIconEl.classList.toggle('bi-play-fill', serverPaused);
    }
  }
  if (settingsBtnEl) {
    settingsBtnEl.classList.toggle('is-open', settingsUi.visible);
  }
  if (lobbySettingsBtnEl) {
    lobbySettingsBtnEl.classList.toggle('is-open', settingsUi.visible);
  }
  if (lobbyNavSettingsBtnEl) {
    lobbyNavSettingsBtnEl.classList.toggle('is-open', settingsUi.visible);
  }
  // Guests cannot chat (server enforces it too); DEV_MODE auto-guests keep
  // the input so local dev commands still work.
  const guestChatLocked = Boolean(authState.guest) && typeof window.DEV_AUTO_GUEST_NAME !== 'string';
  const roomChatMuted = !isLobbyChatSelected() && Boolean(lobby.iAmMuted);
  const selectedChatConnected = isLobbyChatSelected() ? isLobbyChatConnected() : net.connected;
  const chatDisabled = !selectedChatConnected
    || lobbyRuntime.returnToLobbyInProgress
    || guestChatLocked
    || roomChatMuted;
  const commandDisabled = lobbyRuntime.returnToLobbyInProgress || !net.connected;
  replay.updateSidebarElements({ commandDisabled });
  setChatInputEnabled(!chatDisabled);
  if (chatSendBtnEl) {
    chatSendBtnEl.disabled = chatDisabled;
  }
  if (chatGuestLockEl) {
    const lockVisible = guestChatLocked || roomChatMuted;
    chatGuestLockEl.hidden = !lockVisible;
    const lockText = chatGuestLockEl.querySelector('span');
    if (lockText) {
      setTextIfChanged(
        lockText,
        guestChatLocked
          ? "Guests can't chat — sign in to send messages."
          : 'The host muted you in this room.'
      );
    }
  }
  if (chatFormFieldsEl) {
    chatFormFieldsEl.hidden = guestChatLocked || roomChatMuted;
  }
}

function isLiveCommand(text) {
  return /^\/live$/i.test(text);
}

// Replay capture/playback is purely client-side; the only command we relay to
// the server is `/live` (host starts the match).
function sendServerCommand(command) {
  if (!net.connected) {
    setStatus('You are not connected.');
    return false;
  }
  const sent = sendChat(command);
  if (!sent) {
    setStatus('Failed to send slash command.');
  }
  return sent;
}

function showCommandNotice(text, { error = false } = {}) {
  // Successful command output is an ordinary system line. Reserve the red
  // notice treatment for invalid usage and rejected actions.
  showChatNotice(text, undefined, { force: true, kind: error ? 'notice' : '' });
}

function localNetworkStatsText() {
  const smoothDeltaMs = Number(ui.smoothDeltaMs);
  const fps = Number.isFinite(smoothDeltaMs) && smoothDeltaMs > 0
    ? Math.max(1, Math.min(999, Math.round(1000 / smoothDeltaMs)))
    : 0;
  const ping = Math.max(0, Math.round(Number(net.rttMs) || 0));
  const jitter = Math.max(0, Math.round(Number(net.jitterMs) || 0));
  return `FPS: ${fps || 'measuring'} · Ping: ${ping > 0 ? `${ping} ms` : 'measuring'}${jitter > 0 ? ` · Jitter: ${jitter} ms` : ''}`;
}

function handleSidebarUtilityCommand(text) {
  const command = parseChatCommand(text);
  if (!command) return false;

  if (command.name === 'help' || command.name === 'commands') {
    if (isLobbyChatSelected()) {
      const globalCommands = isChatAdmin()
        ? '/mute, /unmute, /ban, /who'
        : '/mute, /unmute';
      showCommandNotice(`Global commands: ${globalCommands}.`);
      return true;
    }
    const hostCommands = !isLobbyChatSelected() && lobby.isHost
      ? ' Host: /mute, /unmute; waiting room: /kick, /ban, /spec.'
      : ' Use /mute and /unmute to hide someone locally.';
    showCommandNotice(`Commands: /stats, /fps, /ping, /who, /live, /stop.${hostCommands}`);
    return true;
  }

  if (
    isLobbyChatSelected()
    && command.name !== 'mute'
    && command.name !== 'unmute'
    && command.name !== 'ban'
    && command.name !== 'who'
  ) {
    showCommandNotice(`/${command.name} is only available in room chat.`, { error: true });
    return true;
  }

  if (command.name === 'stats' || command.name === 'fps' || command.name === 'ping') {
    showCommandNotice(localNetworkStatsText());
    return true;
  }

  if (command.name === 'who') {
    if (isLobbyChatSelected() && !isChatAdmin()) {
      showCommandNotice('Only admins can use /who in global chat.', { error: true });
      return true;
    }
    const names = knownChatNames();
    showCommandNotice(names.length > 0 ? `Here: ${names.join(', ')}` : 'No players are visible here yet.');
    return true;
  }

  if (command.name === 'mute' || command.name === 'unmute') {
    if (!command.target) {
      showCommandNotice(`Usage: /${command.name} <player>`, { error: true });
      return true;
    }
    if (isLobbyChatSelected() && isChatAdmin()) {
      if (!isLobbyChatConnected() || !sendLobbyChat(`/${command.name} ${command.target}`)) {
        showCommandNotice('Global chat is reconnecting.', { error: true });
      }
      return true;
    }
    const serverHostMute = !isLobbyChatSelected()
      && lobby.mode === 'room'
      && lobby.isHost
      && net.connected;
    if (serverHostMute) {
      sendServerCommand(`/${command.name} ${command.target}`);
      return true;
    }
    const result = setLocalChatMuted(command.target, command.name === 'mute');
    showCommandNotice(result.ok
      ? `${result.name} is ${result.muted ? 'now locally muted' : 'no longer locally muted'}.`
      : result.error, { error: !result.ok });
    return true;
  }

  if (command.name === 'kick' || command.name === 'ban' || command.name === 'spec' || command.name === 'spectate') {
    const action = command.name === 'spectate' ? 'spec' : command.name;
    if (!command.target) {
      showCommandNotice(`Usage: /${action} <player>`, { error: true });
      return true;
    }
    if (isLobbyChatSelected()) {
      if (action === 'ban' && isChatAdmin()) {
        if (!isLobbyChatConnected() || !sendLobbyChat(`/ban ${command.target}`)) {
          showCommandNotice('Global chat is reconnecting.', { error: true });
        }
      } else if (action === 'ban') {
        showCommandNotice('Only admins can use /ban in global chat.', { error: true });
      } else {
        showCommandNotice(`/${action} is only available in room chat.`, { error: true });
      }
      return true;
    }
    if (lobby.mode !== 'room' || !net.connected) {
      showCommandNotice(`/${action} is only available inside a room.`, { error: true });
      return true;
    }
    if (!lobby.isHost) {
      showCommandNotice(`Only the host can use /${action}.`, { error: true });
      return true;
    }
    if (lobby.phase === 'running' || lobby.phase === 'starting') {
      showCommandNotice('Kick, ban, and spec are available in the waiting room only.', { error: true });
      return true;
    }
    sendServerCommand(`/${action} ${command.target}`);
    return true;
  }

  return false;
}

function submitSidebarChat() {
  if (!chatInputEl) return;
  // What the field stands for: text, with any emote images back as their
  // tokens (see ui/chat-input.js).
  const text = chatInputText().trim().slice(0, 360);
  if (!text) return;
  if (replay.handleChatCommand(text)) {
    clearChatInput();
    return;
  }
  if (!isLobbyChatSelected() && isLiveCommand(text)) {
    replay.onLiveCommand();
    if (sendServerCommand('/live')) {
      clearChatInput();
    }
    return;
  }
  if (handleSidebarUtilityCommand(text)) {
    clearChatInput();
    return;
  }
  if (isLobbyChatSelected()) {
    if (!isLobbyChatConnected()) {
      setStatus('Lobby chat is reconnecting...');
      return;
    }
    if (sendLobbyChat(text)) {
      clearChatInput();
    } else {
      setStatus('Failed to send chat message.');
    }
    return;
  }
  if (!net.connected) {
    setStatus('You are not connected.');
    return;
  }
  const sent = sendChat(text);
  if (!sent) {
    setStatus('Failed to send chat message.');
    return;
  }
  clearChatInput();
}

function setupSettingsUi() {
  persistSettings();
  syncSettingsModalUi();
  const readFiniteSettingInput = (element, fallback) => {
    if (!element || !('value' in element)) return fallback;
    const parsed = Number(element.value);
    return Number.isFinite(parsed) ? parsed : fallback;
  };
  const toggleControlCapture = (target) => {
    const nextTarget = settingsUi.controlCaptureTarget === target ? null : target;
    setControlCaptureTarget(nextTarget);
    if (nextTarget) {
      clearInput();
    }
    syncSettingsModalUi();
  };

  if (settingsBtnEl) {
    settingsBtnEl.addEventListener('click', (event) => {
      markAudioInteraction(event);
      setSettingsModalVisible(!settingsModalVisible());
    });
  }

  // The rail's Settings item and the chat header's gear open the same modal.
  for (const btn of [lobbySettingsBtnEl, lobbyNavSettingsBtnEl]) {
    btn?.addEventListener('click', (event) => {
      markAudioInteraction(event);
      setSettingsModalVisible(!settingsModalVisible());
    });
  }

  if (settingsCloseBtnEl) {
    settingsCloseBtnEl.addEventListener('click', (event) => {
      markAudioInteraction(event);
      setSettingsModalVisible(false);
    });
  }

  if (settingsModalEl) {
    settingsModalEl.addEventListener('click', (event) => {
      if (event.target === settingsModalEl) {
        setSettingsModalVisible(false);
      }
    });
  }

  if (settingsControlLeftBtnEl) {
    settingsControlLeftBtnEl.addEventListener('click', (event) => {
      markAudioInteraction(event);
      toggleControlCapture('left');
    });
  }

  if (settingsControlRightBtnEl) {
    settingsControlRightBtnEl.addEventListener('click', (event) => {
      markAudioInteraction(event);
      toggleControlCapture('right');
    });
  }

  if (settingsSoundVolumeEl) {
    const updateVolume = () => {
      const nextVolume = clamp(Number(settingsSoundVolumeEl.value) || 0, 0, 100);
      settings.soundVolume = Math.round(nextVolume);
      persistSettings();
      syncSettingsModalUi();
    };
    settingsSoundVolumeEl.addEventListener('input', updateVolume);
    settingsSoundVolumeEl.addEventListener('change', updateVolume);
  }
  if (settingsSoundVolumeValueEl) {
    const updateVolumeFromInput = () => {
      const nextVolume = clamp(readFiniteSettingInput(settingsSoundVolumeValueEl, settings.soundVolume), 0, 100);
      settings.soundVolume = Math.round(nextVolume);
      persistSettings();
      syncSettingsModalUi();
    };
    settingsSoundVolumeValueEl.addEventListener('input', updateVolumeFromInput);
    settingsSoundVolumeValueEl.addEventListener('change', updateVolumeFromInput);
    settingsSoundVolumeValueEl.addEventListener('blur', updateVolumeFromInput);
  }

  if (settingsTurnRadiusEl || settingsTurnRadiusValueEl) {
    const requestTurnRadius = (rawValue, { normalizeNumber = false, source = '' } = {}) => {
      window.clearTimeout(turnRadiusRequestTimer);
      turnRadiusRequestTimer = 0;
      if (!String(rawValue).trim()) {
        if (normalizeNumber && settingsTurnRadiusValueEl) {
          settingsTurnRadiusValueEl.value = String(net.turnRadiusPercent);
        }
        return;
      }
      const parsedValue = Number(rawValue);
      if (!Number.isFinite(parsedValue)) {
        if (normalizeNumber && settingsTurnRadiusValueEl) {
          settingsTurnRadiusValueEl.value = String(net.turnRadiusPercent);
        }
        return;
      }
      const turnRadiusPercent = normalizePlayerTurnRadiusPercent(parsedValue);
      if (settingsTurnRadiusEl && source !== 'slider') {
        settingsTurnRadiusEl.value = String(turnRadiusPercent);
      }
      if (settingsTurnRadiusValueEl && (source === 'slider' || normalizeNumber)) {
        settingsTurnRadiusValueEl.value = String(turnRadiusPercent);
      }
      if (!net.connected || lobby.mode !== 'room' || turnRadiusPercent === net.turnRadiusPercent) return;
      const requestedRoomId = net.roomId;
      turnRadiusRequestTimer = window.setTimeout(() => {
        turnRadiusRequestTimer = 0;
        if (!net.connected || lobby.mode !== 'room' || net.roomId !== requestedRoomId) return;
        sendTurnRadiusSelect(turnRadiusPercent);
      }, TURN_RADIUS_REQUEST_DEBOUNCE_MS);
    };
    if (settingsTurnRadiusEl) {
      settingsTurnRadiusEl.min = String(PLAYER_TURN_RADIUS_MIN_PERCENT);
      settingsTurnRadiusEl.max = String(PLAYER_TURN_RADIUS_MAX_PERCENT);
      settingsTurnRadiusEl.addEventListener('input', () => {
        // Dragging is preview-only: keep the paired number current, but do not
        // send until the range control commits its final value on release.
        window.clearTimeout(turnRadiusRequestTimer);
        turnRadiusRequestTimer = 0;
        const turnRadiusPercent = normalizePlayerTurnRadiusPercent(settingsTurnRadiusEl.value);
        if (settingsTurnRadiusValueEl) {
          settingsTurnRadiusValueEl.value = String(turnRadiusPercent);
        }
      });
      settingsTurnRadiusEl.addEventListener('change', () => {
        requestTurnRadius(settingsTurnRadiusEl.value, { source: 'slider' });
      });
    }
    if (settingsTurnRadiusValueEl) {
      settingsTurnRadiusValueEl.min = String(PLAYER_TURN_RADIUS_MIN_PERCENT);
      settingsTurnRadiusValueEl.max = String(PLAYER_TURN_RADIUS_MAX_PERCENT);
      settingsTurnRadiusValueEl.addEventListener('input', () => {
        requestTurnRadius(settingsTurnRadiusValueEl.value, { source: 'number' });
      });
      settingsTurnRadiusValueEl.addEventListener('change', () => {
        requestTurnRadius(settingsTurnRadiusValueEl.value, { normalizeNumber: true, source: 'number' });
      });
      settingsTurnRadiusValueEl.addEventListener('blur', () => {
        requestTurnRadius(settingsTurnRadiusValueEl.value, { normalizeNumber: true, source: 'number' });
      });
    }
  }

  if (settingsResolutionEl) {
    const updateResolution = () => {
      const nextResolution = clamp(Number(settingsResolutionEl.value) || 100, 50, 100);
      settings.resolutionPercent = Math.round(nextResolution);
      persistSettings();
      fitStageToViewport();
      syncSettingsModalUi();
    };
    settingsResolutionEl.addEventListener('input', updateResolution);
    settingsResolutionEl.addEventListener('change', updateResolution);
  }
  if (settingsResolutionValueEl) {
    const updateResolutionFromInput = () => {
      const nextResolution = clamp(readFiniteSettingInput(settingsResolutionValueEl, settings.resolutionPercent), 50, 100);
      settings.resolutionPercent = Math.round(nextResolution);
      persistSettings();
      fitStageToViewport();
      syncSettingsModalUi();
    };
    settingsResolutionValueEl.addEventListener('input', updateResolutionFromInput);
    settingsResolutionValueEl.addEventListener('change', updateResolutionFromInput);
    settingsResolutionValueEl.addEventListener('blur', updateResolutionFromInput);
  }

  if (settingsInputLagEl) {
    const updateInputLagReduction = () => {
      const nextReduction = clamp(Number(settingsInputLagEl.value) || 0, 0, 50);
      settings.inputLagReductionMs = Math.round(nextReduction);
      persistSettings();
      syncSettingsModalUi();
    };
    settingsInputLagEl.addEventListener('input', updateInputLagReduction);
    settingsInputLagEl.addEventListener('change', updateInputLagReduction);
  }
  if (settingsInputLagValueEl) {
    const updateInputLagReductionFromInput = () => {
      const nextReduction = clamp(readFiniteSettingInput(settingsInputLagValueEl, settings.inputLagReductionMs), 0, 50);
      settings.inputLagReductionMs = Math.round(nextReduction);
      persistSettings();
      syncSettingsModalUi();
    };
    settingsInputLagValueEl.addEventListener('input', updateInputLagReductionFromInput);
    settingsInputLagValueEl.addEventListener('change', updateInputLagReductionFromInput);
    settingsInputLagValueEl.addEventListener('blur', updateInputLagReductionFromInput);
  }

  const bindBooleanSettingToggle = (button, key) => {
    if (!button) return;
    button.addEventListener('click', (event) => {
      markAudioInteraction(event);
      settings[key] = !settings[key];
      persistSettings();
      syncSettingsModalUi();
      syncRenderPrefs();
    });
  };

  bindBooleanSettingToggle(settingsKeyTimestampToggleEl, 'useKeyPressTimestamp');
  bindBooleanSettingToggle(settingsPowerupSpawnToggleEl, 'powerupSpawnSounds');
}

function setupSidebarUi() {
  const devMode = window.DEV_MODE === true;
  if (devReloadBtnEl) {
    devReloadBtnEl.hidden = !devMode;
    if (devMode) {
      devReloadBtnEl.addEventListener('click', (event) => {
        if (lobbyRuntime.returnToLobbyInProgress) return;
        markAudioInteraction(event);
        playSound(SFX.click, 0.2);
        window.location.reload();
      });
    }
  }
  if (muteBtnEl) {
    muteBtnEl.addEventListener('click', (event) => {
      markAudioInteraction(event);
      if (audioState.enabled) {
        playSound(SFX.click, 0.25);
        setSoundEnabled(false);
      } else {
        setSoundEnabled(true);
        playSound(SFX.click, 0.25);
      }
    });
  }
  if (pauseBtnEl) {
    pauseBtnEl.addEventListener('click', (event) => {
      markAudioInteraction(event);
      if (!net.connected) {
        setStatus('You are not connected.');
        return;
      }
      playSound(SFX.click, 0.2);
      togglePause();
    });
  }
  if (lobbyPeekBtnEl) {
    lobbyPeekBtnEl.addEventListener('click', (event) => {
      markAudioInteraction(event);
      playSound(SFX.click, 0.25);
      // Steps out to the browser without giving up the seat, so the snake is
      // still yours to come back to (see returnToLobbyBrowser's keepSeat).
      // Only while a match is live -- outside one there is no seat to hold and
      // leaving should be a clean leave.
      void returnToLobbyBrowser({ keepSeat: matchIsRunningNow() });
    });
  }
  if (stopMatchBtnEl) {
    stopMatchBtnEl.addEventListener('click', (event) => {
      markAudioInteraction(event);
      // Re-checked rather than trusted from the last render: the match can end
      // between the button appearing and the click, and stopping a finished
      // match must do nothing. The server enforces the same thing.
      if (!canStopMatchNow()) return;
      playSound(SFX.click, 0.25);
      sendStopMatch();
    });
  }
  if (leaveBtnEl) {
    leaveBtnEl.addEventListener('click', (event) => {
      if (lobbyRuntime.returnToLobbyInProgress) return;
      markAudioInteraction(event);
      playSound(SFX.click, 0.25);
      // Mid-match this opens the waiting room without leaving the match;
      // the room browser stays one more "Back" away from there.
      if (openWaitingRoomPeek()) return;
      void returnToLobbyBrowser();
    });
  }
  if (chatFormEl) {
    chatFormEl.addEventListener('submit', (event) => {
      event.preventDefault();
      markAudioInteraction(event);
      submitSidebarChat();
    });
  }
  if (chatInputEl) {
    chatInputEl.addEventListener('keydown', (event) => {
      if (event.key !== 'Enter') return;
      event.stopPropagation();
      // A contenteditable is not a form control: Enter neither submits nor is
      // harmless -- left alone it inserts a line break into a single-line
      // field. Both halves have to be done here.
      event.preventDefault();
      markAudioInteraction(event);
      submitSidebarChat();
    });
  }
}

function isEditableTarget(target) {
  if (!(target instanceof HTMLElement)) return false;
  if (target.isContentEditable) return true;
  const tag = target.tagName;
  return tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT';
}

let turnReleaseHandle = 0;
const TURN_TAP_RELEASE_MAX_MS = 80;
const turnKeyDownAtMs = {
  left: 0,
  right: 0
};

function setTurnKey(event, pressed) {
  const nowMs = performance.now();
  const commitTurnState = (sourceEvent = null, force = false) => {
    const changed = applyResolvedTurnKeys();
    sendCurrentInput(force, sourceEvent);
    if (changed) renderImmediateForInput();
  };
  const cancelPendingTurnRelease = () => {
    if (!turnReleaseHandle) return;
    clearTimeout(turnReleaseHandle);
    turnReleaseHandle = 0;
  };
  const scheduleTurnReleaseCommit = (sourceEvent = null) => {
    cancelPendingTurnRelease();
    if (TURN_RELEASE_GRACE_MS <= 0) {
      commitTurnState(sourceEvent, false);
      return;
    }
    turnReleaseHandle = setTimeout(() => {
      turnReleaseHandle = 0;
      commitTurnState(null, false);
    }, TURN_RELEASE_GRACE_MS);
  };
  if (keyMatchesTurnLeft(event)) {
    let holdDurationMs = 0;
    if (pressed) cancelPendingTurnRelease();
    if (pressed) {
      if (!keyIntent.leftDown) turnKeyDownAtMs.left = nowMs;
    } else {
      const pressedAtMs = turnKeyDownAtMs.left || nowMs;
      turnKeyDownAtMs.left = 0;
      holdDurationMs = Math.max(0, nowMs - pressedAtMs);
    }
    keyIntent.leftDown = pressed;
    if (pressed) keyIntent.lastPressedDir = -1;
    if (!pressed && !keyIntent.rightDown) {
      if (holdDurationMs > 0 && holdDurationMs <= TURN_TAP_RELEASE_MAX_MS) {
        commitTurnState(event, false);
      } else {
        scheduleTurnReleaseCommit(event);
      }
    } else {
      commitTurnState(event, false);
    }
    event.preventDefault();
    return true;
  }
  if (keyMatchesTurnRight(event)) {
    let holdDurationMs = 0;
    if (pressed) cancelPendingTurnRelease();
    if (pressed) {
      if (!keyIntent.rightDown) turnKeyDownAtMs.right = nowMs;
    } else {
      const pressedAtMs = turnKeyDownAtMs.right || nowMs;
      turnKeyDownAtMs.right = 0;
      holdDurationMs = Math.max(0, nowMs - pressedAtMs);
    }
    keyIntent.rightDown = pressed;
    if (pressed) keyIntent.lastPressedDir = 1;
    if (!pressed && !keyIntent.leftDown) {
      if (holdDurationMs > 0 && holdDurationMs <= TURN_TAP_RELEASE_MAX_MS) {
        commitTurnState(event, false);
      } else {
        scheduleTurnReleaseCommit(event);
      }
    } else {
      commitTurnState(event, false);
    }
    event.preventDefault();
    return true;
  }
  return false;
}

function isTurnControlKey(input) {
  return keyMatchesTurnLeft(input) || keyMatchesTurnRight(input);
}

function onKeyDown(event) {
  markAudioInteraction(event);
  if (settingsModalVisible()) {
    if (settingsUi.controlCaptureTarget) {
      if (applyCapturedTurnBinding(event)) {
        event.preventDefault();
      }
      return;
    }
    if (event.key === 'Escape') {
      setSettingsModalVisible(false);
      event.preventDefault();
    } else if (isTurnControlKey(event)) {
      event.preventDefault();
    }
    return;
  }
  if (isEditableTarget(event.target)) return;
  if (isLobbyVisible()) {
    if (isTurnControlKey(event)) {
      event.preventDefault();
    }
    return;
  }
  if (setTurnKey(event, true)) return;
}

function onKeyUp(event) {
  if (settingsModalVisible()) {
    if (settingsUi.controlCaptureTarget) {
      event.preventDefault();
      return;
    }
    if (isTurnControlKey(event) || event.key === 'Escape') {
      event.preventDefault();
    }
    return;
  }
  if (isEditableTarget(event.target)) return;
  if (isLobbyVisible()) {
    if (isTurnControlKey(event)) {
      event.preventDefault();
    }
    return;
  }
  setTurnKey(event, false);
}

function clearInput() {
  if (turnReleaseHandle) {
    clearTimeout(turnReleaseHandle);
    turnReleaseHandle = 0;
  }
  turnKeyDownAtMs.left = 0;
  turnKeyDownAtMs.right = 0;
  keyIntent.leftDown = false;
  keyIntent.rightDown = false;
  keyIntent.lastPressedDir = 0;
  const changed = applyResolvedTurnKeys();
  sendCurrentInput(true);
  if (changed) renderImmediateForInput();
}

function renderImmediateForInput() {
  if (isLobbyVisible()) return;
  if (replay.isPlaybackActive()) return;
  if (!ui.hasStateSnapshot) return;
  const nowServer = serverNowMs();
  let renderState = sampleRenderState(nowServer);
  renderState = applyLocalPrediction(renderState, nowServer);
  renderState.serverNow = nowServer;
  model.state = renderState;
  dispatchPlayerFrame(renderState, nowServer, true);
}

// Every pose sampled inside here is timed off the frame clock, so the drawn
// positions are spaced exactly as evenly as the frames that show them.
function frame(realNow) {
  beginRenderFrame(realNow);
  try {
    renderFrame(realNow);
    requestAnimationFrame(frame);
  } finally {
    endRenderFrame();
  }
}

function renderFrame(realNow) {
  if (ui.lastFrameMs > 0 && realNow > 0) {
    const rawDelta = Math.min(100, realNow - ui.lastFrameMs);
    ui.smoothDeltaMs = ui.smoothDeltaMs * 0.85 + rawDelta * 0.15;
  }
  ui.lastFrameMs = realNow || performance.now();

  if (replay.renderFrame(realNow)) {
    return;
  }

  const nowServer = serverNowMs();
  let renderState = sampleRenderState(nowServer);
  renderState = applyLocalPrediction(renderState, nowServer);
  renderState.serverNow = nowServer;
  model.state = renderState;

  flushScheduledStartSfx(Date.now());
  dispatchRenderFrame(renderState, nowServer, !isLobbyVisible());
  updateOverlayCountdown(nowServer);
  updateDelayedMatchEndOverlay(nowServer);
  renderTargetDisplay(nowServer);
  syncLobbyCountdownFromState(nowServer);
  renderLobbyStartCountdown();
  if (ui.scoreDirty) {
    ui.scoreDirty = false;
    renderScores(nowServer);
  }
  if (ui.overlayDirty) {
    ui.overlayDirty = false;
    renderRoundOverlay(nowServer);
  }
  if (ui.controlsDirty) {
    ui.controlsDirty = false;
    updateSidebarControls();
  }
}

window.addEventListener('resize', fitStageToViewport);
window.addEventListener('load', fitStageToViewport);
window.addEventListener('pointerdown', markAudioInteraction, { passive: true });
window.addEventListener('focus', updateAppFocusState);
window.addEventListener('blur', () => {
  clearInput();
  updateAppFocusState();
});
window.addEventListener('beforeunload', () => {
  stopTimers();
  disconnect();
});
document.addEventListener('visibilitychange', updateAppFocusState);
document.addEventListener('keydown', onKeyDown, { passive: false });
document.addEventListener('keyup', onKeyUp, { passive: false });

async function bootstrapApp() {
  if (queryParam('netdebug') === '1') {
    window.curvegenNetDebug = Object.freeze({
      // Per-player flags as the client currently holds them, hydrated. The
      // server omits the alive/connected/drawingTrail block on every frame
      // where it did not change, so "what is the client actually rendering
      // from" is not answerable from the wire alone.
      players: () => (model.state?.players || []).map((player) => ({
        id: player?.id,
        alive: player?.alive,
        connected: player?.connected
      })),
      // The room roster as the client holds it. Score-panel labels resolve from
      // here first (playerLabelById), so "why is that row called P2" is a
      // question about this and not about the snapshot.
      roster: () => (lobby.players || []).map((player) => ({
        playerId: player?.playerId,
        name: player?.name,
        inRoom: player?.inRoom
      })),
      snapshot: () => ({
        ...net.debugStats,
        rttMs: net.rttMs,
        jitterMs: net.jitterMs,
        snapshotBufferEntries: net.snapshotBuffer.length,
        outstandingPings: net.pingSentAt.size,
        pendingInputs: net.pendingInputs.length,
        predictionHistoryEntries: net.predictionInputHistory.length
      })
    });
  }
  setCanvasSize(model.config.fieldWidth, model.config.fieldHeight);
  fitStageToViewport();
  await setupRenderBackend();
  clearField();
  setNetworkDeps({
    applyState,
    pushSnapshot,
    updatePredictionAnchor,
    applyAckSeq,
    sendCurrentInput,
    inputHeartbeatIntervalMs,
    clearAllInputRetries,
    clearInput
  });
  setLobbyDeps({
    startNetworking,
    disconnect,
    stopTimers,
    clearInput
  });
  if (window.DEV_MODE === true) {
    const replayModule = await import('./ui/replay-controls.js');
    Object.assign(replay, replayModule.createReplayControls({ updateSidebarControls }));
    replay.setupSidebar();
  }
  setupNetworkHandlers();
  setupAuthUi();
  setupLobbyUi();
  setupSidebarUi();
  setupChatChannels();
  setupChatEmotes();
  setupChatAutocomplete();
  initHighScores();
  initProfile();
  // Admin message deletion goes through the channel that owns the line.
  setChatDeleteHandler((id, feedId) => {
    if (feedId === LOBBY_CHAT_FEED_ID) {
      sendLobbyChatDelete(id);
    } else if (net.connected && feedId === net.roomId) {
      sendChatDelete(id);
    }
  });
  setupSettingsUi();
  initFocusOverlay();
  resetChatFeed('', true);
  renderScores();
  renderRoundOverlay();
  updateSidebarControls();
  ui.scoreDirty = false;
  ui.overlayDirty = false;
  ui.controlsDirty = false;
  bootstrapAuthAndStart();
  requestAnimationFrame(frame);
}

void bootstrapApp();
